/**
 * Chat History Hook
 *
 * Manages chat sessions and provides persistence to the server.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { chatApi, isAuthenticated } from '../services/api';

const ChatHistoryContext = createContext(null);

const PAGE_SIZE = 20; // Load 20 sessions at a time

export function ChatHistoryProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  // Load sessions on mount
  useEffect(() => {
    if (isAuthenticated()) {
      loadSessions();
    }
  }, []);

  const loadSessions = useCallback(async () => {
    console.log('📂 loadSessions called, isAuthenticated:', isAuthenticated());
    if (!isAuthenticated()) return;

    setLoading(true);
    setError(null);
    setOffset(0);

    try {
      const data = await chatApi.getSessions(PAGE_SIZE, 0);
      const newSessions = data.sessions || [];
      console.log('📂 Loaded sessions:', newSessions.length, newSessions.map(s => s.title?.substring(0, 20)));
      setSessions(newSessions);
      setHasMore(newSessions.length === PAGE_SIZE);
      setOffset(newSessions.length);

      // DON'T auto-select a session on load
      // User should either:
      // 1. Click on an existing session to continue it (loads messages)
      // 2. Start typing to create a new session
      // This matches ChatGPT behavior
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  }, [currentSessionId]);

  const loadMoreSessions = useCallback(async () => {
    if (!isAuthenticated() || loadingMore || !hasMore) return;

    setLoadingMore(true);

    try {
      const data = await chatApi.getSessions(PAGE_SIZE, offset);
      const newSessions = data.sessions || [];

      if (newSessions.length > 0) {
        setSessions(prev => [...prev, ...newSessions]);
        setOffset(prev => prev + newSessions.length);
      }

      setHasMore(newSessions.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more sessions:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [offset, loadingMore, hasMore]);

  const createSession = useCallback(async (title = null, dbId = 'kfc_postgres') => {
    console.log('📂 createSession called, isAuthenticated:', isAuthenticated());
    if (!isAuthenticated()) return null;

    try {
      const session = await chatApi.createSession(title, dbId);
      console.log('📂 Created session:', session.id, session.title);
      setSessions(prev => {
        console.log('📂 Updating sessions, prev count:', prev.length, 'new count:', prev.length + 1);
        return [session, ...prev];
      });
      setCurrentSessionId(session.id);
      return session;
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Failed to create new session');
      return null;
    }
  }, []);

  const selectSession = useCallback(async (sessionId) => {
    setCurrentSessionId(sessionId);

    // Load session messages
    try {
      const session = await chatApi.getSession(sessionId);
      return session;
    } catch (err) {
      console.error('Failed to load session:', err);
      return null;
    }
  }, []);

  const updateSession = useCallback(async (sessionId, updates) => {
    try {
      const updated = await chatApi.updateSession(sessionId, updates);
      setSessions(prev =>
        prev.map(s => (s.id === sessionId ? updated : s))
      );
      return updated;
    } catch (err) {
      console.error('Failed to update session:', err);
      return null;
    }
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    try {
      await chatApi.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));

      // If deleting current session, switch to another
      if (currentSessionId === sessionId) {
        const remaining = sessions.filter(s => s.id !== sessionId);
        setCurrentSessionId(remaining[0]?.id || null);
      }
      return true;
    } catch (err) {
      console.error('Failed to delete session:', err);
      return false;
    }
  }, [currentSessionId, sessions]);

  const pinSession = useCallback(async (sessionId, isPinned) => {
    return updateSession(sessionId, { is_pinned: isPinned });
  }, [updateSession]);

  const addMessage = useCallback(async (message, overrideSessionId = null) => {
    // Use override session ID if provided, otherwise use current session
    const sessionId = overrideSessionId || currentSessionId;
    if (!sessionId || !isAuthenticated()) return null;

    try {
      const saved = await chatApi.addMessage(sessionId, message);

      // Auto-set session title from first user message
      if (message.role === 'user' && message.content) {
        const targetSession = sessions.find(s => s.id === sessionId);
        // Only set title if session has no title or default title
        if (targetSession && (!targetSession.title || targetSession.title === 'New Conversation' || targetSession.title === 'محادثة جديدة')) {
          // Create title with time suffix to distinguish duplicate questions
          const timeStr = new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });

          // Truncate question to 40 chars to leave room for time
          const questionPart = message.content.length > 40
            ? message.content.substring(0, 37) + '...'
            : message.content;

          const title = `${questionPart} (${timeStr})`;

          // Update session title
          await updateSession(sessionId, { title });
        }
      }

      return saved;
    } catch (err) {
      console.error('Failed to save message:', err);
      return null;
    }
  }, [currentSessionId, sessions, updateSession]);

  const getOrCreateSession = useCallback(async (dbId = 'kfc_postgres') => {
    if (currentSessionId) return currentSessionId;

    const session = await createSession(null, dbId);
    return session?.id || null;
  }, [currentSessionId, createSession]);

  const value = {
    sessions,
    currentSessionId,
    loading,
    loadingMore,
    hasMore,
    error,
    loadSessions,
    loadMoreSessions,
    createSession,
    selectSession,
    updateSession,
    deleteSession,
    pinSession,
    addMessage,
    getOrCreateSession,
    setCurrentSessionId,
  };

  return (
    <ChatHistoryContext.Provider value={value}>
      {children}
    </ChatHistoryContext.Provider>
  );
}

export function useChatHistory() {
  const context = useContext(ChatHistoryContext);

  if (!context) {
    throw new Error('useChatHistory must be used within ChatHistoryProvider');
  }

  return context;
}

export default useChatHistory;
